/**
 * AccountTypeBadge — visual indicator of a ``SocialAccount.account_type``.
 *
 * Backend stores a normalized token per connected account (see
 * ``docs/social-account-types-tracking.md`` for the full vocabulary).
 * This component maps that token to a human label + pill color so users
 * can tell a Business Instagram from a Personal one at a glance.
 *
 * Design rules:
 *   - Unknown / missing ``account_type`` renders a neutral "Unknown"
 *     chip, NOT nothing. That way legacy rows that haven't been
 *     backfilled stay visible and obviously-incomplete rather than
 *     silently looking like a regular Personal account.
 *   - Color intent: business/brand variants use a warm solid color
 *     (amber/blue), personal variants use a cool neutral, verified
 *     uses a dedicated blue tick-adjacent color.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Briefcase,
  User,
  Building2,
  Sparkles,
  CheckCircle2,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';

interface AccountTypeBadgeProps {
  /** Normalized token from the backend. Null/undefined is treated
   * as a legacy row that hasn't been backfilled yet. */
  accountType: string | null | undefined;
  /** Used to disambiguate when the same token appears on multiple
   * platforms (currently unused but reserved for future per-platform
   * label tweaks). */
  platform?: string;
  /** Render smaller so it fits inline next to the account name. */
  size?: 'sm' | 'md';
}

interface BadgeStyle {
  label: string;
  bg: string;
  fg: string;
  icon: LucideIcon;
}

/** One entry per supported ``account_type`` token. Keep in sync with
 * ``backend/app/services/social/oauth.py::SUPPORTED_ACCOUNT_TYPES``. */
const BADGE_STYLES: Record<string, BadgeStyle> = {
  // Business / branded variants — amber warm accent.
  instagram_business: {
    label: 'Business',
    bg: 'bg-amber-50',
    fg: 'text-amber-700',
    icon: Briefcase,
  },
  instagram_creator: {
    label: 'Creator',
    bg: 'bg-purple-50',
    fg: 'text-purple-700',
    icon: Sparkles,
  },
  instagram_personal: {
    label: 'Personal',
    bg: 'bg-slate-100',
    fg: 'text-slate-600',
    icon: User,
  },
  facebook_page: {
    label: 'Page',
    bg: 'bg-blue-50',
    fg: 'text-blue-700',
    icon: Building2,
  },
  facebook_personal: {
    label: 'Personal',
    bg: 'bg-slate-100',
    fg: 'text-slate-600',
    icon: User,
  },
  twitter_personal: {
    label: 'Personal',
    bg: 'bg-slate-100',
    fg: 'text-slate-600',
    icon: User,
  },
  twitter_verified: {
    label: 'Verified',
    bg: 'bg-sky-50',
    fg: 'text-sky-700',
    icon: CheckCircle2,
  },
  tiktok_business: {
    label: 'Business',
    bg: 'bg-amber-50',
    fg: 'text-amber-700',
    icon: Briefcase,
  },
  tiktok_personal: {
    label: 'Personal',
    bg: 'bg-slate-100',
    fg: 'text-slate-600',
    icon: User,
  },
  linkedin_personal: {
    label: 'Personal',
    bg: 'bg-slate-100',
    fg: 'text-slate-600',
    icon: User,
  },
  linkedin_company: {
    label: 'Company',
    bg: 'bg-blue-50',
    fg: 'text-blue-700',
    icon: Building2,
  },
  youtube_personal: {
    label: 'Personal',
    bg: 'bg-slate-100',
    fg: 'text-slate-600',
    icon: User,
  },
  youtube_brand: {
    label: 'Brand',
    bg: 'bg-red-50',
    fg: 'text-red-700',
    icon: Briefcase,
  },
  pinterest_business: {
    label: 'Business',
    bg: 'bg-amber-50',
    fg: 'text-amber-700',
    icon: Briefcase,
  },
  pinterest_personal: {
    label: 'Personal',
    bg: 'bg-slate-100',
    fg: 'text-slate-600',
    icon: User,
  },
  bluesky_personal: {
    label: 'Personal',
    bg: 'bg-slate-100',
    fg: 'text-slate-600',
    icon: User,
  },
  threads_personal: {
    label: 'Personal',
    bg: 'bg-slate-100',
    fg: 'text-slate-600',
    icon: User,
  },
  telegram_bot: {
    label: 'Bot',
    bg: 'bg-sky-50',
    fg: 'text-sky-700',
    icon: Sparkles,
  },
  tumblr_personal: {
    label: 'Personal',
    bg: 'bg-slate-100',
    fg: 'text-slate-600',
    icon: User,
  },
  wordpress_personal: {
    label: 'Personal',
    bg: 'bg-slate-100',
    fg: 'text-slate-600',
    icon: User,
  },
  gmb_business: {
    label: 'Business',
    bg: 'bg-amber-50',
    fg: 'text-amber-700',
    icon: Briefcase,
  },
};

const UNKNOWN_STYLE: BadgeStyle = {
  label: 'Unknown type',
  bg: 'bg-slate-50',
  fg: 'text-slate-500',
  icon: HelpCircle,
};

export default function AccountTypeBadge({
  accountType,
  size = 'sm',
}: AccountTypeBadgeProps) {
  const { t } = useTranslation();
  const style =
    (accountType && BADGE_STYLES[accountType]) || UNKNOWN_STYLE;
  const Icon = style.icon;
  const iconSize = size === 'md' ? 12 : 10;
  const textCls = size === 'md' ? 'text-[11px]' : 'text-[10px]';
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-semibold tracking-tight ${style.bg} ${style.fg} ${textCls}`}
      title={t('account_type_title', 'Account type: {{label}}', {
        label: style.label,
      })}
    >
      <Icon size={iconSize} aria-hidden="true" />
      {style.label}
    </span>
  );
}

/** Exposed for tests + for the frontend's dedup logic so platform
 * pages can ask "which types are supported for this platform?" */
export const SUPPORTED_ACCOUNT_TYPES: Record<string, readonly string[]> = {
  instagram: ['instagram_business', 'instagram_creator', 'instagram_personal'],
  facebook: ['facebook_page'],
  twitter: ['twitter_personal', 'twitter_verified'],
  tiktok: ['tiktok_business', 'tiktok_personal'],
  linkedin: ['linkedin_personal', 'linkedin_company'],
  youtube: ['youtube_personal', 'youtube_brand'],
  pinterest: ['pinterest_business', 'pinterest_personal'],
  bluesky: ['bluesky_personal'],
  threads: ['threads_personal'],
  telegram: ['telegram_bot'],
  tumblr: ['tumblr_personal'],
  wordpress: ['wordpress_personal'],
  gmb: ['gmb_business'],
};

/** Variants we let the user explicitly choose in the Connect modal.
 * A subset of ``SUPPORTED_ACCOUNT_TYPES`` — excludes variants that are
 * detected server-side (e.g. twitter_verified) or that represent a
 * rejection state (facebook_personal). */
export const CONNECTABLE_VARIANTS: Record<string, readonly string[]> = {
  instagram: ['instagram_business'],
  tiktok: ['tiktok_business', 'tiktok_personal'],
  linkedin: ['linkedin_personal', 'linkedin_company'],
  youtube: ['youtube_personal', 'youtube_brand'],
  pinterest: ['pinterest_business', 'pinterest_personal'],
};
